import { useEffect, useState } from 'react';
import { ButtonProps } from '../../common/button/Button';
import Map from '../../common/map/Map';
import PropertiesHeader from '../../common/propertiesHeader/PropertiesHeader';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useWaWorkplaceService from 'src/api/wa/service/WaWorkplaceService';
import { useWaWorkplaceStore } from 'src/api/wa/store/WaWorkplaceStore';
import { WaScreensMap } from '../../router/WardsScreenMap';
import { WA_Screen } from '../../router/WardsScreens';
import { wardsNavigate } from '../../router/WardsScreenRegistration';
import { useWaUserStore } from 'src/api/wa/store/WaUserStore';
import { useWaFilterStore } from 'src/api/wa/store/WaFilterStore';
import { useWaDisasterStore } from 'src/api/wa/store/WaRegionAndDisasterStore';
import { LoadingSpinner } from '../../desktop/components/common/loading/LoadingSpinner';

export function S3_1_PropertiesMap() {
  const { user, availableWorkers } = useWaUserStore();
  const { workplaces } = useWaWorkplaceStore();
  const { fetchWorkplaces, fetchUserSalesOffice } = useWaWorkplaceService();
  const { selectedDisaster } = useWaDisasterStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { areMandatorySearchParametersSet } = useWaFilterStore();
  const { mapCenter, setMapCenter } = useWaFilterStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);

  useEffect(() => {
    if (!mapCenter) {
      // User's location is set as map center if available.
      const userLocation = user?.location;
      if (userLocation?.latitude != null && userLocation?.longitude != null) {
        setMapCenter(userLocation);
      }
      // When Map (Mobile) screen is initially shown, need to load user's office location to show the pin on map.
      if (user) {
        fetchUserSalesOffice(user?.branchCode, user?.salesOfficeCode);
      }
    }
  }, [user?.location, mapCenter, setMapCenter, fetchUserSalesOffice, user]);

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      setIsTimeout(false);
      try {
        await fetchWorkplaces();
      } catch (error) {
        if ((error as { code?: string }).code === 'ECONNABORTED') {
          setIsTimeout(true);
        }
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    loadInitialData();
  }, [fetchWorkplaces]);

  const shouldShowNoSitesMessage =
    !isLoading &&
    !isTimeout &&
    workplaces !== null &&
    areMandatorySearchParametersSet() &&
    workplaces.length === 0;

  const handleRefreshClick = async () => {
    setIsLoading(true);
    setIsTimeout(false);
    try {
      await fetchWorkplaces();
    } catch (error) {
      if ((error as { code?: string }).code === 'ECONNABORTED') {
        setIsTimeout(true);
      }
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReservedPropertiesClick = () => {
    wardsNavigate(navigate, WaScreensMap[WA_Screen.AddressedWorkplacesList].path);
  };

  const handleMenuClick = () => {
    wardsNavigate(navigate, WaScreensMap[WA_Screen.RecoverySupportMenu].path);
  };

  const handleChangeSearchConditionsClick = () => {
    wardsNavigate(navigate, WaScreensMap[WA_Screen.SearchFilter].path);
  };

  const buttons: ButtonProps[] = [
    {
      key: 'refresh',
      title: t('wards.common.refresh'),
      onClick: handleRefreshClick,
    },
    {
      key: 'listProperties',
      title: t('wards.header.reservedPropertyList'),
      onClick: handleReservedPropertiesClick,
    },
    {
      key: 'seachCondition',
      title: t('wards.header.changeSearchCondition'),
      onClick: handleChangeSearchConditionsClick,
    },
    {
      key: 'menu',
      title: t('wards.common.menu'),
      onClick: handleMenuClick,
    },
  ];

  return (
    <div className="flex h-screen flex-col">
      <PropertiesHeader title={`${t('wards.sites.map.unaddressedProperties')}`} buttons={buttons} />
      <div className="relative flex-1">
        {isLoading ? (
          <div className="flex h-full items-center justify-center bg-gray-100">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {shouldShowNoSitesMessage && (
              <h3 className="mx-2 my-4 text-center  text-xl">{t('wards.common.noResults')}</h3>
            )}
            {isTimeout && (
              <h3 className="mx-2 my-4 text-center text-xl text-red-500">
                {t('wards.common.searchTimeout')}
              </h3>
            )}
            {!selectedDisaster ? (
              <div className="flex h-full items-center justify-center">
                <h2 className="text-xl">{t('wards.desktop.menu.noWideAreaDisaster')}</h2>
              </div>
            ) : (
              <Map
                user={user}
                workplaces={workplaces || []}
                workers={availableWorkers}
                legendButtonVisible={true}
                center={mapCenter}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
